import type { Layer } from './grid';

/**
 * Hand-built hands for the 48 px icons: drawn directly on the 48 grid (not scaled from the 24
 * art), with four skin tones, rounded fingers, a cuff and a gold bracer. Origin (x, y) is the
 * top-left in 48 px; the hand is about 24 x 24.
 */
const S0 = '#ffe6cc';
const S1 = '#f4c9a0';
const S2 = '#c48458';
const S3 = '#8a5a3a';
const CL = '#f4f1ea';
const CL2 = '#b8b0a0';
const RD = '#dc4430';
const GD = '#f6cf4a';
const GD2 = '#a8721c';

/** A rounded rect on the 48 grid, corner radius r. */
function rr(L: Layer, x: number, y: number, w: number, h: number, r: number, c: string): void {
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const cx = i < r ? r - 1 - i : i >= w - r ? i - (w - r) : -1;
      const cy = j < r ? r - 1 - j : j >= h - r ? j - (h - r) : -1;
      if (cx >= 0 && cy >= 0 && cx * cx + cy * cy > (r - 0.3) * (r - 0.3)) continue;
      L.raw(x + i, y + j, c);
    }
}

function cuff(L: Layer, x: number, y: number, w: number): void {
  rr(L, x, y, w, 5, 1, CL);
  L.raw(x, y + 4, CL2);
  for (let i = 0; i < w; i++) {
    L.raw(x + i, y + 4, CL2);
    L.raw(x + i, y + 5, RD);
  }
  rr(L, x + 1, y + 6, w - 2, 3, 1, GD);
  for (let i = 1; i < w - 1; i++) L.raw(x + i, y + 8, GD2);
}

export function fist48(L: Layer, x: number, y: number): void {
  // four knuckles
  for (let i = 0; i < 4; i++) {
    const fx = x + i * 6;
    rr(L, fx, y, 6, 14, 3, S1);
    for (let j = 1; j < 11; j++) L.raw(fx, y + j, S2); // left shade
    for (let j = 2; j < 7; j++) L.raw(fx + 4, y + j, S0); // upper-right light
    L.raw(fx + 3, y + 1, S0);
    for (let j = 4; j < 13; j++) L.raw(fx + 5, y + j, S2);
    L.raw(fx + 1, y + 9, S3); // curl crease
    L.raw(fx + 2, y + 9, S3);
    L.raw(fx + 3, y + 9, S3);
  }
  for (const sx of [6, 12, 18]) for (let j = 2; j < 13; j++) L.raw(x + sx, y + j, S3);
  // thumb across the fingers
  rr(L, x + 1, y + 12, 17, 6, 3, S1);
  for (let i = 3; i < 16; i++) {
    L.raw(x + i, y + 12, S0);
    L.raw(x + i, y + 17, S2);
  }
  for (let i = 3; i < 15; i++) L.raw(x + i, y + 13, S0);
  L.raw(x + 2, y + 15, S3);
  cuff(L, x + 2, y + 18, 20);
}

export function palm48(L: Layer, x: number, y: number): void {
  const hs = [13, 17, 19, 15];
  hs.forEach((h, i) => {
    const fx = x + 3 + i * 5;
    const top = y + 18 - h;
    rr(L, fx, top, 5, h + 2, 2, S1);
    for (let j = 2; j < h; j++) L.raw(fx, top + j, S2);
    for (let j = 1; j < h - 3; j++) L.raw(fx + 3, top + j, S0);
    L.raw(fx + 2, top + 1, S0);
    for (let j = 3; j < h + 1; j++) L.raw(fx + 4, top + j, S3);
    // knuckle crease
    L.raw(fx + 1, top + Math.floor(h / 2), S2);
    L.raw(fx + 2, top + Math.floor(h / 2), S2);
    L.raw(fx + 3, top + Math.floor(h / 2), S2);
  });
  // palm
  rr(L, x + 2, y + 14, 22, 12, 4, S1);
  for (let i = 4; i < 20; i++) L.raw(x + 3 + i, y + 14, S0);
  for (let j = 16; j < 24; j++) L.raw(x + 2, y + j, S2);
  for (let i = 5; i < 21; i++) L.raw(x + i, y + 25, S2);
  // thumb
  for (let t = 0; t < 8; t++) rr(L, x - 3 + t, y + 14 - Math.floor(t / 2) + 6 - t, 6, 6, 3, t % 3 === 0 ? S1 : S1);
  L.raw(x - 1, y + 17, S0);
  L.raw(x, y + 16, S0);
  // palm lines
  for (let i = 0; i < 6; i++) L.raw(x + 8 + i, y + 21 + (i > 3 ? -1 : 0), S2);
  cuff(L, x + 4, y + 26, 18);
}
